import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, CalendarClock, ChevronsUpDown, Download, Info, Layers,
  ReceiptText, Search, TriangleAlert, Wallet, X,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Button, EntityAvatar, FilterResetButton, Input, Skeleton, Tooltip, cn } from '../../ui';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { useUrlString } from '../../hooks/useUrlState';
import { ExportDialog } from '../../components/ExportDialog';
import { RetailerLink } from '../../components/EntityLinks';
import { TerritoryChips } from '../../components/TerritoryChips';
import { Empty, Kpi, Panel } from '../dashboards/dashboardParts';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inrShort = (v: any): string => {
  const n = Number(v ?? 0);
  const a = Math.abs(n);
  if (a >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (a >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
  return inr(n);
};
const pctOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const retailerWord = (n: number) => `${n} ${n === 1 ? 'retailer' : 'retailers'}`;

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * What the buckets actually mean
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Every bucket counts DAYS PAST A BILL'S DUE DATE — the invoice date plus that retailer's own credit
 * period — NOT days since the invoice. That is the fact the previous version of this page never
 * stated anywhere, which is why "0 – 30 days" read as a riddle: on a 45-day retailer, a bill raised
 * this morning and one raised six weeks ago both landed in it.
 *
 * Worse, the server's `<= 30` arm also swallowed every NEGATIVE datediff, so money still inside its
 * credit period — normally most of the book, and nothing anyone may chase — was filed under a label
 * that implied lateness. `b_not_due` now splits that out (see `outstandingAging`), which is what
 * turns the ladder below into a story: healthy money at the top, money to chase underneath it.
 *
 * `fill` is a 5-step green→red ramp used for the ladder bars, the row dots and the bucket columns
 * alike, so one colour means one level of lateness everywhere on the page. Colour never carries the
 * meaning alone — every bucket is named beside its swatch, and `meaning` spells the range out in
 * words at the point a reader is most likely to be confused.
 */
const BUCKETS = [
  {
    key: 'b_not_due', label: 'Not due yet', short: 'not due', col: 'Not due',
    meaning: 'Billed, still inside the retailer’s credit period — nothing to chase.',
    fill: '#0ca30c', tone: 'text-brand-600 dark:text-brand-400',
  },
  {
    key: 'b0_30', label: '1 – 30 days late', short: '1–30 late', col: '1–30 late',
    meaning: 'The credit period ran out up to a month ago.',
    fill: '#fab219', tone: 'text-harvest-700 dark:text-harvest-300',
  },
  {
    key: 'b31_60', label: '31 – 60 days late', short: '31–60 late', col: '31–60 late',
    meaning: 'One to two months past the due date.',
    fill: '#ef8f3c', tone: 'text-[#c47a25] dark:text-[#f3b880]',
  },
  {
    key: 'b61_90', label: '61 – 90 days late', short: '61–90 late', col: '61–90 late',
    meaning: 'Two to three months past the due date.',
    fill: '#e0603c', tone: 'text-[#c2603a] dark:text-[#f0a184]',
  },
  {
    key: 'b_gt90', label: 'Over 90 days late', short: '90+ late', col: '90+ late',
    meaning: 'More than three months past the due date — the money least likely to arrive.',
    fill: '#d03b3b', tone: 'text-danger-600 dark:text-danger-500',
  },
] as const;

type BucketKey = typeof BUCKETS[number]['key'];
const LATE_KEYS = ['b0_30', 'b31_60', 'b61_90', 'b_gt90'] as const;

interface Row {
  retailerId: string; name: string; code: string; city: string; territoryId: string | null; photoUrl: string | null;
  creditLimit: number; creditDays: number | null;
  b_not_due: number; b0_30: number; b31_60: number; b61_90: number; b_gt90: number;
  total: number; overdue: number; bills: number;
  oldestDaysPastDue: number | null; nextDueInDays: number | null;
  worst: typeof BUCKETS[number] | null;
}
type Totals = Record<BucketKey, number> & { total: number; overdue: number; bills: number };

/**
 * The lenses the KPI band offers, in the order a collections call is actually made.
 *
 * A lens is a QUESTION, not a bucket: "what is safe", "what is late at all", "what is nearly lost".
 * `match` is what each one keeps, so the tile, the retailer count under it and the filtered table can
 * never drift apart — all three are computed from this one predicate.
 */
type LensKey = 'all' | 'not_due' | 'overdue' | 'critical';
const LENSES: Record<LensKey, (r: Row) => boolean> = {
  all: (r) => r.total > 0,
  not_due: (r) => r.b_not_due > 0,
  overdue: (r) => r.overdue > 0,
  critical: (r) => r.b_gt90 > 0,
};

/** The oldest bucket a retailer still has money in — what decides how urgently they are chased. */
function worstBucket(row: any): typeof BUCKETS[number] | null {
  for (let i = BUCKETS.length - 1; i >= 0; i--) {
    if (Number(row[BUCKETS[i].key] ?? 0) > 0) return BUCKETS[i];
  }
  return null;
}

/**
 * How old this retailer's oldest money is, in words.
 *
 * Prefers the server's exact day count over the bucket name, because "62d late" is something a rep
 * can repeat on the phone and "61–90 late" is not. Falls back to the bucket when the figure is
 * missing, and states when the next bill falls due for a retailer who owes nothing late — a blank cell
 * there reads as missing data rather than as good news.
 */
function ageLabel(r: Row): { text: string; late: boolean } {
  if (r.oldestDaysPastDue != null && r.oldestDaysPastDue >= 0) {
    const d = r.oldestDaysPastDue;
    return { text: d === 0 ? 'due today' : `${d}d late`, late: d > 0 };
  }
  if (r.overdue > 0) return { text: r.worst?.short ?? 'late', late: true };
  if (r.nextDueInDays != null) return { text: `due in ${r.nextDueInDays}d`, late: false };
  return { text: 'not due', late: false };
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Sorting
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

type SortKey = 'name' | 'oldest' | 'bills' | BucketKey | 'total';

/**
 * Sorting is CLIENT-side here, unlike the paginated registers: `/outstanding/aging` returns the whole
 * book in one response (one row per retailer with an open balance), so there is nothing to page through
 * and a round trip per column click would buy nothing.
 *
 * The default order is OLDEST MONEY FIRST, not biggest — a retailer sitting on ₹500 that is ninety days
 * late is a harder problem than one with ₹50,000 raised last week, and sorting by total alone buries
 * exactly the rows this page exists for. Any column overrides it, and the choice lives in the URL so
 * a shared link opens the same view.
 */
function compare(a: Row, b: Row, key: SortKey): number {
  if (key === 'name') return a.name.localeCompare(b.name);
  // A retailer with nothing overdue has no age at all; parking them below every aged row keeps the
  // "oldest" sort meaning what it says rather than shuffling them in as if they were zero days late.
  if (key === 'oldest') return (b.oldestDaysPastDue ?? -99999) - (a.oldestDaysPastDue ?? -99999);
  if (key === 'bills') return b.bills - a.bills;
  if (key === 'total') return b.total - a.total;
  return (a[key] === b[key] ? b.total - a.total : b[key] - a[key]);
}
const defaultOrder = (a: Row, b: Row) =>
  (b.b_gt90 - a.b_gt90) || (b.b61_90 - a.b61_90) || (b.b31_60 - a.b31_60)
  || (b.b0_30 - a.b0_30) || (b.total - a.total);

/** A sortable header cell. The arrow appears only on the column actually doing the sorting. */
function SortTh({ label, sortKey, active, desc, onSort, className, title, align = 'right' }: {
  label: string; sortKey: SortKey; active: boolean; desc: boolean;
  onSort: (k: SortKey) => void; className?: string; title?: string; align?: 'left' | 'right';
}) {
  return (
    <th scope="col" className={cn('py-2 pr-2 font-medium', className)}>
      <Tooltip title={title}>
        <button
          type="button" onClick={() => onSort(sortKey)}
          className={cn(
            // `uppercase` is repeated here on purpose: a <button> does not inherit the header row's
            // text-transform, so without it the sortable headers render in sentence case beside a
            // shouting "CREDIT LIMIT".
            'group inline-flex max-w-full items-center gap-1 rounded-control px-1 py-0.5 uppercase transition-colors',
            'hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            align === 'right' && 'justify-end',
            active ? 'text-content' : 'text-subtle',
          )}
        >
          <span className="truncate">{label}</span>
          {/* Sort icon always trails the label on the right — the app-wide convention. */}
          {active
            ? (desc ? <ArrowDown className="size-3 shrink-0" /> : <ArrowUp className="size-3 shrink-0" />)
            : <ChevronsUpDown className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />}
        </button>
      </Tooltip>
    </th>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export function OutstandingAgingPage() {
  // Server gate is OUTSTANDING.EXPORT — deliberately NOT OUTSTANDING.VIEW, which a retailer holds for
  // their own dues: this document names every retailer's credit limit and how far past due they are.
  const canExport = usePermission('OUTSTANDING.EXPORT');
  const [exportOpen, setExportOpen] = useUrlString<'' | '1'>('export', '');

  // Filter and sort live in the URL, so Back out of a retailer returns the view you were working.
  const [lens, setLens] = useUrlString<LensKey>('lens', 'all');
  const [bucket, setBucket] = useUrlString<BucketKey | ''>('bucket', '');
  const [q, setQ] = useUrlString('q', '');
  const [sort, setSort] = useUrlString<SortKey | ''>('sort', '');
  const [dir, setDir] = useUrlString<'asc' | 'desc'>('dir', 'desc');

  const { data, isLoading } = useQuery({
    queryKey: ['aging'],
    queryFn: () => request<any[]>({ url: '/outstanding/aging' }),
  });

  // Resolve retailerId → the retailer's name, town and credit terms (gated; falls back to #id).
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const { data: retailersResp } = useQuery({
    queryKey: ['aging-retailers'],
    queryFn: () => requestAllPages('/retailers'),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailersResp?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailersResp]);

  const rows = useMemo<Row[]>(() => (data ?? []).map((r: any) => {
    const ret = retailerMap.get(String(r.retailerId));
    const nums = Object.fromEntries(BUCKETS.map((b) => [b.key, Number(r[b.key] ?? 0)])) as Record<BucketKey, number>;
    return {
      ...nums,
      retailerId: String(r.retailerId),
      name: ret ? ret.companyName : (r.companyName ?? `#${r.retailerId}`),
      code: ret?.code ?? '',
      city: ret?.city ?? '',
      // Where the dealer sits. Chasing money is field work: who calls on this shop follows from
      // which beat it is in, and the row used to say only its code and its town.
      territoryId: ret?.territoryId ? String(ret.territoryId) : null,
      // The shop photo when the record carries one; `EntityAvatar` falls back to initials on a hue
      // derived from the name, so a retailer without a photo still gets a stable identity mark.
      photoUrl: ret?.photoUrl ?? null,
      creditLimit: Number(ret?.creditLimit ?? 0),
      creditDays: ret?.creditPeriodDays == null ? null : Number(ret.creditPeriodDays),
      total: Number(r.total ?? 0),
      overdue: LATE_KEYS.reduce((n, k) => n + nums[k], 0),
      bills: Number(r.bills ?? 0),
      oldestDaysPastDue: r.oldestDaysPastDue == null ? null : Number(r.oldestDaysPastDue),
      nextDueInDays: r.nextDueInDays == null ? null : Number(r.nextDueInDays),
      worst: worstBucket(r),
    };
  }), [data, retailerMap]);

  /**
   * Portfolio totals — computed from the WHOLE book, never from the filtered list.
   *
   * The tiles ARE the filter control, so if they re-totalled whatever the filter left behind then
   * pressing "Overdue" would make the overdue figure equal the total figure, and the band would stop
   * being a measurement of anything.
   */
  const totals = useMemo<Totals>(() => {
    const t: any = { total: 0, overdue: 0, bills: 0 };
    for (const b of BUCKETS) t[b.key] = 0;
    for (const r of rows) {
      for (const b of BUCKETS) t[b.key] += r[b.key];
      t.total += r.total; t.overdue += r.overdue; t.bills += r.bills;
    }
    return t as Totals;
  }, [rows]);

  /** How many retailers sit in each lens and each bucket — the count that makes a tile worth pressing. */
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const k of Object.keys(LENSES) as LensKey[]) c[k] = rows.filter(LENSES[k]).length;
    for (const b of BUCKETS) c[b.key] = rows.filter((r) => r[b.key] > 0).length;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = rows
      .filter(LENSES[lens] ?? LENSES.all)
      .filter((r) => !bucket || r[bucket] > 0)
      .filter((r) => !needle
        || r.name.toLowerCase().includes(needle)
        || r.code.toLowerCase().includes(needle)
        || r.city.toLowerCase().includes(needle));
    return sort
      ? [...list].sort((a, b) => (dir === 'desc' ? 1 : -1) * compare(a, b, sort))
      : [...list].sort(defaultOrder);
  }, [rows, lens, bucket, q, sort, dir]);

  /** What the filtered view adds up to — stated on the table, never mixed into the tiles above. */
  const shownTotals = useMemo(() => {
    const t: any = { total: 0, bills: 0 };
    for (const b of BUCKETS) t[b.key] = 0;
    for (const r of filtered) {
      for (const b of BUCKETS) t[b.key] += r[b.key];
      t.total += r.total; t.bills += r.bills;
    }
    return t as Totals;
  }, [filtered]);

  const filterOn = lens !== 'all' || !!bucket || !!q.trim();
  const clearAll = () => { setLens('all'); setBucket(''); setQ(''); };

  /** Pressing a tile that is already on clears it — a filter you cannot switch off is a trap. */
  const pickLens = (k: LensKey) => { setBucket(''); setLens(lens === k ? 'all' : k); };
  const pickBucket = (k: BucketKey) => { setLens('all'); setBucket(bucket === k ? '' : k); };
  const onSort = (k: SortKey) => {
    if (sort === k) { setDir(dir === 'desc' ? 'asc' : 'desc'); return; }
    setSort(k); setDir(k === 'name' ? 'asc' : 'desc');
  };

  const activeLabel = bucket
    ? BUCKETS.find((b) => b.key === bucket)?.label
    : lens === 'not_due' ? 'Not due yet'
      : lens === 'overdue' ? 'Overdue'
        : lens === 'critical' ? 'Over 90 days late' : null;

  return (
    <div className="space-y-4">
      {/* ── What this page is, and what its numbers are counted from ────────────────────────── */}
      <header className="rounded-card border border-line bg-surface shadow-e1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 py-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-content">Outstanding ageing</h1>
            {/* Ageing is always "as on today" — the buckets are counted from today's date, so there is
                no period picker: offering one would promise a historical snapshot this report cannot
                produce. Saying so is cheaper than fielding the question. */}
            <p className="mt-1 text-sm text-muted">
              What every retailer owes, split by how long each bill has been past its due date
              {' · as on '}
              <span className="font-medium text-content">{dayjs().format('D MMM YYYY')}</span>
            </p>
          </div>
          {canExport && (
            <Button size="sm" icon={<Download size={15} />} onClick={() => setExportOpen('1')}>Export</Button>
          )}
        </div>
        {/* The one sentence the old page was missing. "These ranges make no sense" comes from not
            knowing the clock starts at the DUE DATE, not at the invoice — so the page says it, once,
            where the reader meets the buckets rather than in a tooltip they have to go hunting for. */}
        <p className="flex items-start gap-2 border-t border-line px-5 py-2.5 text-xs text-muted">
          <Info className="mt-px size-3.5 shrink-0 text-brand-600" aria-hidden />
          <span>
            A bill’s clock starts on its <span className="font-medium text-content">due date</span> —
            the tax-invoice date plus that retailer’s credit period. So{' '}
            <span className="font-medium text-content">“31 – 60 days late”</span> means the credit
            period ran out one to two months ago, not that the invoice is two months old.
          </span>
        </p>
      </header>

      {/* ── The four questions the book gets asked. Each tile filters the table below. ───────── */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Kpi
          icon={<Wallet />} tone="sky" label="Total owed to us" value={inrShort(totals.total)}
          sub={`${retailerWord(counts.all)} · ${totals.bills} open ${totals.bills === 1 ? 'bill' : 'bills'}`}
          onClick={() => pickLens('all')} active={lens === 'all' && !bucket} loading={isLoading}
        />
        <Kpi
          icon={<CalendarClock />} tone="brand" label="Not due yet" value={inrShort(totals.b_not_due)}
          sub={totals.b_not_due > 0
            ? `${pctOf(totals.b_not_due, totals.total)}% of the book · inside credit terms`
            : 'every bill is past its due date'}
          onClick={() => pickLens('not_due')} active={lens === 'not_due'} loading={isLoading}
        />
        <Kpi
          icon={<TriangleAlert />} tone={totals.overdue > 0 ? 'gold' : 'slate'} label="Overdue"
          value={inrShort(totals.overdue)}
          sub={totals.overdue > 0
            ? `${retailerWord(counts.overdue)} late · ${pctOf(totals.overdue, totals.total)}% of the book`
            : 'nothing is past its due date'}
          onClick={() => pickLens('overdue')} active={lens === 'overdue'} loading={isLoading}
        />
        <Kpi
          icon={<Layers />} tone={totals.b_gt90 > 0 ? 'danger' : 'slate'} label="Over 90 days late"
          value={inrShort(totals.b_gt90)}
          sub={totals.b_gt90 > 0 ? `${retailerWord(counts.b_gt90)} · chase these first` : 'nothing this late'}
          onClick={() => pickLens('critical')} active={lens === 'critical'} loading={isLoading}
        />
      </div>

      {/* ── The ladder: every bucket, what it means, and what sits in it ─────────────────────── */}
      <Panel
        icon={<Layers />} title="How late the money is"
        action={<span className="text-xs text-muted">{inr(totals.total)} across {retailerWord(counts.all)}</span>}
        bodyClassName="p-0"
      >
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 rounded-control" />)}
          </div>
        ) : totals.total > 0 ? (
          /*
           * The ladder is a DATA TABLE with fixed measure columns (label, bar, amount, %, retailers),
           * and below ~380px those columns cannot all fit however they are trimmed. The brief allows a
           * data table to scroll; it does not allow the PAGE to. So the ladder scrolls inside its own
           * box — `min-w-max` keeps the columns aligned, `overflow-x-auto` keeps the document still.
           */
          <div className="overflow-x-auto"><div className="min-w-max sm:min-w-0">
            {/* The rungs' widths are mirrored here rather than shared, because this is the ladder's
                COLUMN HEADER: the track was a bar with no denominator — a fill you cannot read a
                quantity off — so the scale is named once, over the column, instead of repeating
                "of ₹9,797" on all five rows. */}
            {/* Gap and padding mirror the rungs below at every breakpoint — the header is what sets
                the row's minimum width, so shrinking only the rungs left the page still overflowing. */}
            <div className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-[11px] font-medium uppercase tracking-[0.06em] text-subtle sm:gap-3 sm:px-4">
              <span className="size-2.5 shrink-0" aria-hidden />
              <span className="w-28 shrink-0 sm:w-36">How late</span>
              <span className="hidden min-w-0 max-w-md flex-1 lg:block">What that means</span>
              <span className="hidden min-w-32 max-w-sm flex-1 sm:block">How full</span>
              <span className="w-40 shrink-0 text-right">In this range / total</span>
              <span className="w-9 shrink-0 text-right">%</span>
              <span className="hidden w-20 shrink-0 text-right md:block">Retailers</span>
            </div>
            <ul>
            {BUCKETS.map((b) => {
              const v = totals[b.key];
              const pct = pctOf(v, totals.total);
              const on = bucket === b.key;
              return (
                <li key={b.key} className="border-b border-line/60 last:border-0">
                  {/* Each rung is the filter for its own bucket: the reader's next question after
                      "how much is 61–90 late?" is always "who?", and it is one click away. */}
                  <button
                    type="button" onClick={() => pickBucket(b.key)} aria-pressed={on} disabled={v === 0}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors sm:gap-3 sm:px-4',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
                      v === 0 ? 'cursor-default opacity-45' : 'hover:bg-surface-2',
                      on && 'bg-surface-2',
                    )}
                  >
                    <span className="size-2.5 shrink-0 rounded-full" style={{ background: b.fill }} aria-hidden />
                    <span className="w-28 shrink-0 truncate text-sm font-medium text-content sm:w-36">{b.label}</span>
                    <span className="hidden min-w-0 max-w-md flex-1 truncate text-xs text-subtle lg:block">{b.meaning}</span>
                    {/* Part-to-whole one rung at a time: each bar is that bucket's share of the book,
                        so the five read as a ladder instead of a stacked bar's unreadable slivers.
                        The track takes the row's spare width, so a wide screen stretches the measure
                        rather than opening a hole in the middle of the rung. */}
                    <span
                      className="hidden h-2 min-w-32 max-w-sm flex-1 overflow-hidden rounded-full bg-surface-3 sm:block"
                      role="img" aria-label={`${inr(v)} of ${inr(totals.total)} — ${pct}%`}
                      title={`${inr(v)} of ${inr(totals.total)}`}
                    >
                      <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: b.fill }} />
                    </span>
                    {/* A partly-filled track states a proportion but not a quantity — you cannot read
                        "how much, out of what" off a fill. So every rung spells the fraction out,
                        the same `part / whole` shape the Credit left column uses. Right-aligned at a
                        fixed width, so the five numerators still line up for comparison. */}
                    <span className="w-40 shrink-0 text-right text-sm tabular-nums">
                      <span className="font-semibold text-content">{inr(v)}</span>
                      <span className="text-subtle"> / {inr(totals.total)}</span>
                    </span>
                    <span className="w-9 shrink-0 text-right text-xs tabular-nums text-subtle">{pct}%</span>
                    <span className="hidden w-20 shrink-0 text-right text-xs tabular-nums text-subtle md:block">
                      {v > 0 ? retailerWord(counts[b.key]) : '—'}
                    </span>
                  </button>
                </li>
              );
            })}
            </ul>
          </div></div>
        ) : <Empty title="Nothing outstanding" detail="Every retailer is square with us." />}
      </Panel>

      {/* ── Who owes it ─────────────────────────────────────────────────────────────────────── */}
      <Panel
        icon={<ReceiptText />} title="Who owes it"
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {activeLabel && (
              // The band and the ladder filter from a distance, so the table has to say out loud what
              // it is showing — otherwise a narrowed list looks like data that failed to load.
              <button
                type="button" onClick={clearAll}
                className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-2 py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-content transition-colors hover:border-line-strong"
              >
                {activeLabel}
                <X className="size-3.5 text-subtle" aria-hidden />
                <span className="sr-only">Clear filter</span>
              </button>
            )}
            <Input
              allowClear prefix={<Search className="size-3.5" />} placeholder="Find a retailer, code or town" aria-label="Find a retailer, code or town"
              value={q} onChange={(e: any) => setQ(e.target.value)} style={{ width: 210 }}
            />
          </div>
        }
        bodyClassName="p-0"
      >
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-control" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Empty
            title={filterOn ? 'No retailer matches that' : 'Nothing outstanding'}
            detail={filterOn ? 'Try a different bucket, or clear the search.' : 'Every retailer is square with us.'}
            action={filterOn
              ? <FilterResetButton onClick={clearAll} className="mt-2" />
              : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            {/* Ten columns of real facts rather than six and a lake of whitespace. The retailer cell no
                longer absorbs every spare pixel, and a row now answers "how old, how many bills, how
                much room is left on their limit" without anyone opening the retailer. */}
            <table className="w-full min-w-[86rem] table-fixed text-sm">
              <thead className="gb-thead-tint">
                <tr className="border-b border-line text-left text-[11px] font-medium uppercase tracking-[0.06em] text-subtle">
                  <SortTh label="Retailer" sortKey="name" align="left" className="w-[19rem] pl-4 text-left"
                    active={sort === 'name'} desc={dir === 'desc'} onSort={onSort} />
                  <SortTh label="Oldest" sortKey="oldest" className="w-[6.5rem] text-right"
                    active={sort === 'oldest'} desc={dir === 'desc'} onSort={onSort}
                    title="Days past the due date of this retailer’s oldest unpaid bill" />
                  <SortTh label="Bills" sortKey="bills" className="w-[4.5rem] text-right"
                    active={sort === 'bills'} desc={dir === 'desc'} onSort={onSort}
                    title="Unpaid bills making up the total" />
                  {BUCKETS.map((b) => (
                    <SortTh key={b.key} label={b.col} sortKey={b.key} className="w-[7rem] text-right"
                      active={sort === b.key} desc={dir === 'desc'} onSort={onSort} title={b.meaning} />
                  ))}
                  <SortTh label="Total" sortKey="total" className="w-[10.5rem] text-right"
                    active={sort === 'total'} desc={dir === 'desc'} onSort={onSort}
                    title="Everything this retailer owes" />
                  <th scope="col" className="w-[10.5rem] py-2 pr-4 text-right font-medium">Credit left</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const age = ageLabel(r);
                  const overLimit = r.creditLimit > 0 && r.total > r.creditLimit;
                  return (
                    <tr key={r.retailerId} className="border-b border-line/60 transition-colors last:border-0 hover:bg-surface-2">
                      <td className="min-w-0 py-2 pl-4 pr-2">
                        <div className="flex items-center gap-2.5">
                          {/* Identity mark first, severity dot on its shoulder: one tells rows apart
                              while scrolling, the other sorts the book into fine / watch / chase. */}
                          <span className="relative shrink-0">
                            <EntityAvatar name={r.name} src={r.photoUrl} size={30} />
                            <span
                              className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-surface"
                              style={{ background: r.worst?.fill ?? 'var(--gb-border-strong)' }} aria-hidden
                            />
                          </span>
                          <span className="min-w-0">
                            {/* Name and dealer code on ONE line — both are identity, and the code is
                                what the desk quotes and types. The line below is then purely WHERE
                                they are, instead of a number and a place sharing a row. */}
                            <span className="flex min-w-0 items-baseline gap-1.5">
                              {/* `min-w-0` on the link and `wrap` on the chips keep both lines inside the
                                  19rem cell — a long name pushed the code, and a long territory chain
                                  pushed "Narayangaon", across the Oldest column ("due in 29d"). */}
                              <RetailerLink id={r.retailerId} className="min-w-0">
                                <span className="block truncate font-medium">{r.name}</span>
                              </RetailerLink>
                              {r.code && <span className="shrink-0 text-[11px] text-subtle">{r.code}</span>}
                            </span>
                            <span className="mt-0.5 flex min-w-0 items-center">
                              <TerritoryChips territoryId={r.territoryId} city={r.city} hideWhenEmpty wrap />
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <span className={cn('text-xs font-medium tabular-nums',
                          age.late ? (r.worst?.tone ?? 'text-content') : 'text-subtle')}>
                          {age.text}
                        </span>
                      </td>
                      <td className="py-2 pr-2 text-right text-xs tabular-nums text-muted">{r.bills || '—'}</td>
                      {BUCKETS.map((b) => {
                        const v = r[b.key];
                        return (
                          <td key={b.key} className="py-2 pr-2 text-right tabular-nums">
                            {/* A zero is drawn away, not bolded into noise — the eye should land on
                                the buckets that actually hold money. */}
                            {v > 0 ? <span className={cn('font-medium', b.tone)}>{inr(v)}</span>
                              : <span className="text-subtle">—</span>}
                          </td>
                        );
                      })}
                      <td className="py-2 pr-2 text-right">
                        {/* This used to be a mini-bar scaled to the largest row on screen — a
                            denominator that was never stated and silently changed whenever a filter
                            narrowed the list, so the same retailer's bar grew without them owing a
                            rupee more. Replaced by the reading itself, against a fixed whole: the
                            same `part / total` shape the ladder and the Credit left column use. */}
                        <span className="block text-sm tabular-nums">
                          <span className="font-semibold text-content">{inr(r.total)}</span>
                          <span className="text-subtle"> / {inr(totals.total)}</span>
                        </span>
                        <span className="block text-[11px] tabular-nums text-subtle">
                          {pctOf(r.total, totals.total)}% of the book
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {r.creditLimit > 0 ? (
                          <span className="block">
                            {/* LEFT over LIMIT, in that order: the headroom is the figure a credit
                                decision is made on, and the sanctioned limit is the scale it has to
                                be read against. Printing the limit alone — as this column first did —
                                states the one number nobody needs to look up. */}
                            <span className="block text-xs tabular-nums">
                              <span className={cn('font-medium',
                                overLimit ? 'text-harvest-700 dark:text-harvest-300' : 'text-content')}>
                                {overLimit ? `−${inr(r.total - r.creditLimit)}` : inr(r.creditLimit - r.total)}
                              </span>
                              <span className="text-subtle"> / {inr(r.creditLimit)}</span>
                            </span>
                            {/* Naming the gap in amber is the convention the other registers use:
                                "over by ₹X" is actionable where a red total is only alarming. */}
                            <span className={cn('block text-[11px] tabular-nums',
                              overLimit ? 'font-medium text-harvest-700 dark:text-harvest-300' : 'text-subtle')}>
                              {overLimit ? `over by ${inr(r.total - r.creditLimit)}` : 'left'}
                              {r.creditDays ? ` · ${r.creditDays}d terms` : ''}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-subtle">no limit set</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {/* The foot totals what is ON SCREEN, and says so when a filter is on: a total that
                    silently means something narrower than the band above it is how a report gets
                    quoted wrong in a meeting. */}
                <tr className="border-t border-line-strong bg-surface-2/60 text-sm font-semibold">
                  <td className="py-2.5 pl-4 pr-2 text-muted">
                    {filterOn ? 'Shown' : 'Total'} · {retailerWord(filtered.length)}
                  </td>
                  <td />
                  <td className="py-2.5 pr-2 text-right text-xs tabular-nums text-muted">{shownTotals.bills}</td>
                  {BUCKETS.map((b) => (
                    <td key={b.key} className="py-2.5 pr-2 text-right tabular-nums text-content">
                      {inr(shownTotals[b.key])}
                    </td>
                  ))}
                  <td className="py-2.5 pr-2 text-right tabular-nums text-content">{inr(shownTotals.total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      <ExportDialog
        open={exportOpen === '1'}
        onClose={() => setExportOpen('')}
        title="Export receivables ageing"
        description="Every retailer's dues split into not-due / 1-30 / 31-60 / 61-90 / over-90 days past the due date, oldest money first."
        baseUrl="/outstanding/aging/export"
        hidePeriod
      />
    </div>
  );
}
